import type { ReconciliationPeriod } from "@/store/extractionAnnualized/reconciliation";
import React from "react";
import clsx from "clsx";

import { MUIGlobalStyles as GlobalStyles } from "@proda-ai/ui-kit";

import type {
    AgGridReact,
    Column,
    FocusGridInnerElementParams,
    GetRowIdFunc,
    GridProps,
} from "@/ui/molecules/Grid";
import Grid from "@/ui/molecules/Grid";

import type {
    TColumnDefsOperatingStatementView,
    TRowAnnualizationWithOperatingStatements,
    TRowReviewWithOperatingStatements,
    TRowTotalPinned,
} from "../typesGrids";

import { useExpandableSubcategories } from "../useExpandableSubcategories";

import { makeGridAnnualizationColumnDefs } from "./makeGridAnnualizationColumnDefs";

export type GridAnnualizationProps = Omit<
    GridProps<TRowReviewWithOperatingStatements>,
    "columnDefs" | "rowData" | "readOnlyEdit" | "onCellEditRequest"
> & {
    reconciliationPeriods?: ReconciliationPeriod[];
    projectId: number;
    columnDefsOperatingStatements: TColumnDefsOperatingStatementView;
    data: TRowAnnualizationWithOperatingStatements[];
    totalNetIncome: TRowTotalPinned | null;
    extractionsInfo:
        | {
              fileName: string;
              extractionId: number;
              sheetName: string;
              uploadedAt: string | undefined;
              uploadedBy: string | undefined;
              uploadedByUserId?: number;
              completedAt: string | null | undefined;
          }[]
        | null;
    selectedAnnualizationGroup: "month" | "quarter" | "half" | "year";
    showSource: boolean;
    longestOperatingStatementAndTotalsNumericValue: number;
    getExtractionReviewUrl: (params: {
        projectId: number;
        extractionId: number;
    }) => string;
    onViewExtraction: ({
        projectId,
        extractionId,
    }: {
        projectId: number;
        extractionId: number;
    }) => void;
};
const extraStyles = {
    "& .ag-header-cell, .ag-header-group-cell": {
        border: "none !important",
    },
    "& .ag-row-level-0": {
        backgroundColor: "var(--proda-white) !important",
    },
    "&.ag-theme-custom-proda": {
        "--ag-cell-horizontal-padding": "0 !important",
    },
};
export const testIds = { main: "GridAnnualization" };
const GridAnnualization = React.memo(
    React.forwardRef(
        (
            props: GridAnnualizationProps,
            ref: React.ForwardedRef<AgGridReact>,
        ) => {
            const {
                reconciliationPeriods,
                data,
                projectId,
                columnDefsOperatingStatements:
                    columnDefsOperatingStatementsFromProps,
                totalNetIncome,
                extractionsInfo,
                selectedAnnualizationGroup,
                showSource,
                longestOperatingStatementAndTotalsNumericValue,
                getExtractionReviewUrl,
                onViewExtraction,
                ...otherProps
            } = props;

            const columnDefs = React.useMemo(
                () =>
                    makeGridAnnualizationColumnDefs({
                        reconciliationPeriods,
                        projectId,
                        columnDefsOperatingStatementsFromProps,
                        longestOperatingStatementAndTotalsNumericValue,
                        extractionsInfo,
                        onViewExtraction,
                        getExtractionReviewUrl,
                        selectedAnnualizationGroup,
                        showSource,
                    }),
                [
                    reconciliationPeriods,
                    projectId,
                    columnDefsOperatingStatementsFromProps,
                    longestOperatingStatementAndTotalsNumericValue,
                    extractionsInfo,
                    onViewExtraction,
                    getExtractionReviewUrl,
                    selectedAnnualizationGroup,
                    showSource,
                ],
            );
            const focusFirstCellInAccountColumn = React.useCallback(
                (
                    params: FocusGridInnerElementParams<TRowReviewWithOperatingStatements>,
                ) => {
                    const column = params.api.getColumn("account") as Column;
                    params.api.setFocusedCell(0, column, null);
                    return true;
                },
                [],
            );

            const getRowId: GetRowIdFunc<TRowReviewWithOperatingStatements> =
                React.useCallback(params => String(params.data.rowId), []);

            const { visibleRows, context, onCellMouseDown } =
                useExpandableSubcategories(data);

            return (
                <>
                    <GlobalStyles styles={extraStyles} />
                    <Grid<TRowReviewWithOperatingStatements>
                        ref={ref}
                        className={clsx([testIds.main])}
                        gridTheme="ag-theme-custom-proda"
                        context={context}
                        onCellMouseDown={onCellMouseDown}
                        rowData={visibleRows}
                        pinnedBottomRowData={
                            totalNetIncome
                                ? [
                                      {
                                          rowId: "row-altered",
                                          account: "Net Income Total",
                                          type: "TOTAL",
                                          reconciliationFavorableAbove: true,
                                          ...totalNetIncome,
                                      },
                                  ]
                                : []
                        }
                        columnDefs={columnDefs}
                        getRowId={getRowId}
                        defaultColDef={{
                            suppressHeaderMenuButton: true,
                            sortable: false,
                        }}
                        groupHeaderHeight={26}
                        headerHeight={
                            selectedAnnualizationGroup === "year" ? 48 : 26
                        }
                        readOnlyEdit={true}
                        suppressHeaderFocus={true}
                        suppressMovableColumns={true}
                        enableBrowserTooltips={true}
                        focusGridInnerElement={focusFirstCellInAccountColumn}
                        rowStyle={{ backgroundColor: "none" }}
                        enterNavigatesVertically={true}
                        enterNavigatesVerticallyAfterEdit={true}
                        {...otherProps}
                    />
                </>
            );
        },
    ),
);

GridAnnualization.displayName = "GridAnnualization";

export default GridAnnualization;
GridAnnualization.displayName = "GridAnnualization";
